import { TEXTOS } from '../../../lib/glossario'
import type { StatusGmud } from '../../../types/gmud'
import {
  ETAPAS,
  ETAPA_COM_DIVERGENCIA,
  etapaAtual,
  proximaAcaoDaEtapa,
  RESPONSAVEL_NA_TELA,
  type EtapaDoProcesso,
} from './etapasDoAnexoXxi'

/**
 * Onde a solicitação está no processo do contrato, e de quem se está esperando.
 *
 * **A etiqueta de status responde "o quê"; esta lista responde "de quem".** Um estado como
 * "Em verificação" não diz a ninguém se a bola está com a contratada ou com a Prefeitura — e é essa
 * a pergunta que trava uma solicitação por dias. O ANEXO XXI atribui responsável a cada etapa, e é
 * essa atribuição que aparece aqui.
 *
 * **A etapa é derivada do status, e não guardada.** Um segundo lugar dizendo "em que etapa está"
 * divergiria do status no primeiro dia em que alguém corrigisse um pelo psql.
 *
 * @param props o estado atual da solicitação
 * @returns a projeção das sete etapas
 */
export function EtapasDaGmud({ status }: { status: StatusGmud }) {
  const t = TEXTOS.etapas
  const atual = etapaAtual(status)

  return (
    <section className="cartao p-4" aria-label={t.titulo}>
      <h2 className="text-base font-semibold text-slate-900">{t.titulo}</h2>
      <p className="mt-1 max-w-prose text-sm text-slate-600">{t.ajuda}</p>

      {atual == null && <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{t.foraDoFluxo}</p>}

      <ol className="mt-4 space-y-1">
        {ETAPAS.map((etapa) => (
          <Etapa key={etapa.numero} etapa={etapa} atual={atual} />
        ))}
      </ol>

      <p className="mt-3 text-xs text-slate-500">{t.divergenciaDaEtapa7}</p>
    </section>
  )
}

/** Uma etapa: cumprida, em curso ou por vir — e de quem ela é. */
function Etapa({ etapa, atual }: { etapa: EtapaDoProcesso; atual: number | null }) {
  const t = TEXTOS.etapas
  const cumprida = atual != null && etapa.numero < atual
  const emCurso = atual === etapa.numero
  const proximaAcao = proximaAcaoDaEtapa(etapa.numero)

  return (
    <li
      className={
        emCurso
          ? 'flex gap-3 rounded-lg bg-niteroi-50 px-3 py-2 ring-1 ring-inset ring-niteroi-200'
          : 'flex gap-3 px-3 py-2'
      }
      aria-current={emCurso ? 'step' : undefined}
    >
      <span
        className={
          cumprida
            ? 'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-semibold text-emerald-900'
            : emCurso
              ? 'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-niteroi-700 text-xs font-semibold text-white'
              : 'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500'
        }
        aria-hidden="true"
      >
        {cumprida ? '✓' : etapa.numero}
      </span>

      <div className="min-w-0">
        <p className={emCurso ? 'text-sm font-semibold text-slate-900' : 'text-sm font-medium text-slate-800'}>
          {etapa.titulo}
        </p>
        {/* Responsável numa linha própria: na coluna estreita ao lado das ações, "· responsável:"
            colado ao título quebrava no meio e o leitor perdia de quem era a etapa. */}
        <p className="text-xs text-slate-600">
          {t.responsavel(RESPONSAVEL_NA_TELA[etapa.responsavel])}
          {etapa.numero === ETAPA_COM_DIVERGENCIA && <span className="text-amber-800"> {t.marcaDaDivergencia}</span>}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">{etapa.resumo}</p>
        {emCurso && proximaAcao && (
          <p className="mt-1 text-xs font-semibold text-niteroi-800">{t.esperandoPor(RESPONSAVEL_NA_TELA[proximaAcao])}</p>
        )}
      </div>
    </li>
  )
}
